// Typed connector-definition registry for the Integrations catalog
// (roadmap #9 Phase 1, D4). Truthful entries only - a connector appears
// here iff the backend/agent code that runs it actually exists today; see
// docs/INTEGRATION_TOOL_PHASE0_AUDIT.md's D4 section for the verification
// of each one. Owner decision (2026-09-16): include all 7 verified
// connectors, with the 3 agent-only ones clearly disclosed rather than
// hidden - see each entry's `agentSetupHint`.
//
// `sourceType`/`provider` are the Mapping fields this connector produces
// (`Mapping.sourceType` + `spec.provider` for the webhook family) - not a
// new backend concept, just how the catalog maps onto the existing
// resource. Prometheus Remote Write and Prometheus scrape share the same
// `sourceType: "prometheus"` Mapping shape; they differ only in which side
// initiates the transfer (see internal/promwrite/handler.go's header
// comment), so they're two catalog entries over one Mapping shape.

export type ConnectorCategory = 'engineering' | 'metrics' | 'business' | 'custom';

// 'in_product' = the web app can configure and activate this today.
// 'agent_required' = it works, but only via the customer running the
// cue-cli agent themselves - there is no in-product install flow yet
// (see docs/INTEGRATION_TOOL_PHASE0_AUDIT.md D4). Never silently treat
// these the same as 'in_product' - the catalog card must disclose this.
export type ConnectorSetupKind = 'in_product' | 'agent_required';

export interface ConnectorDefinition {
  id: string;
  name: string;
  category: ConnectorCategory;
  description: string;
  setupKind: ConnectorSetupKind;
  sourceType: string;
  provider?: string;
  // Plain-text pointer to real, existing setup instructions - never a
  // fabricated URL. Set only when setupKind is 'agent_required'.
  agentSetupHint?: string;
}

const CUE_CLI_IMPORT_HINT =
  'Requires the cue-cli agent. Install it and run `cue-cli import` (see zagentc/README.md, "Import mode").';

export const connectorCatalog: ConnectorDefinition[] = [
  {
    id: 'github',
    name: 'GitHub Actions',
    category: 'engineering',
    description: 'Workflow runs and deployment statuses become CI pass/fail and deploy Signals.',
    setupKind: 'in_product',
    sourceType: 'webhook',
    provider: 'github',
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    category: 'business',
    description: 'Connect a Salesforce org via OAuth; Zmist polls Opportunities, Leads or Cases on a schedule and maps them to Signals.',
    setupKind: 'in_product',
    sourceType: 'salesforce',
  },
  {
    id: 'prometheus_remote_write',
    name: 'Prometheus Remote Write',
    category: 'metrics',
    description: "Point Prometheus's remote_write directly at Zmist - no agent, no scrape.",
    setupKind: 'in_product',
    sourceType: 'prometheus',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'business',
    description: 'Stripe events (charges, subscriptions, disputes) become Signals via a signature-verified webhook.',
    setupKind: 'in_product',
    sourceType: 'webhook',
    provider: 'stripe',
  },
  {
    id: 'webhook',
    name: 'Webhook',
    category: 'custom',
    description: 'Any HTTP POST becomes Signals, authenticated with a bearer token you choose.',
    setupKind: 'in_product',
    sourceType: 'webhook',
    provider: 'generic',
  },
  {
    id: 'prometheus_scrape',
    name: 'Prometheus scrape',
    category: 'metrics',
    description: 'An agent polls a /metrics endpoint on an interval and forwards samples.',
    setupKind: 'agent_required',
    sourceType: 'prometheus',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'json',
    name: 'JSON via Agent',
    category: 'custom',
    description: 'An agent polls a JSON endpoint or local file and maps records to Signals.',
    setupKind: 'agent_required',
    sourceType: 'json',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'csv',
    name: 'CSV via Agent',
    category: 'custom',
    description: 'An agent reads a CSV endpoint or local file and maps rows to Signals.',
    setupKind: 'agent_required',
    sourceType: 'csv',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'xml',
    name: 'XML via Agent',
    category: 'custom',
    description: 'An agent reads an XML endpoint or local file; `recordPath` picks the repeating element.',
    setupKind: 'agent_required',
    sourceType: 'xml',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'tsv',
    name: 'TSV via Agent',
    category: 'custom',
    description: 'An agent reads a tab-separated endpoint or local file and maps rows to Signals.',
    setupKind: 'agent_required',
    sourceType: 'tsv',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'yaml',
    name: 'YAML via Agent',
    category: 'custom',
    description: 'An agent reads a YAML endpoint or local file and maps records to Signals.',
    setupKind: 'agent_required',
    sourceType: 'yaml',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'lines',
    name: 'Text lines via Agent',
    category: 'custom',
    description: 'An agent reads a log-like text endpoint, file or piped stream (JSON lines, key=value, or plain text) and maps each line to Signals.',
    setupKind: 'agent_required',
    sourceType: 'lines',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  {
    id: 'system',
    name: 'Host metrics via Agent',
    category: 'metrics',
    description: 'An agent reports this machine\'s CPU, RAM, disk (df/du), temperature, disk I/O, network, load and uptime - no URL or login to configure.',
    setupKind: 'agent_required',
    sourceType: 'system',
    agentSetupHint: CUE_CLI_IMPORT_HINT,
  },
  // Scraper: Zmist itself fetches the URL on a schedule (internal/
  // scrapequeue) - no agent, no local install. Distinct `sourceType`
  // strings (scrape_*) from the agent_required entries above of the same
  // format, so the backend can tell a Scraper mapping apart from an
  // agent-pulled one (see internal/integration.Resolve's comment).
  {
    id: 'scrape_json',
    name: 'JSON via Scraper',
    category: 'custom',
    description: 'Zmist polls a JSON endpoint on a schedule and maps records to Signals.',
    setupKind: 'in_product',
    sourceType: 'scrape_json',
  },
  {
    id: 'scrape_csv',
    name: 'CSV via Scraper',
    category: 'custom',
    description: 'Zmist polls a CSV endpoint on a schedule and maps rows to Signals.',
    setupKind: 'in_product',
    sourceType: 'scrape_csv',
  },
  {
    id: 'scrape_tsv',
    name: 'TSV via Scraper',
    category: 'custom',
    description: 'Zmist polls a tab-separated endpoint on a schedule and maps rows to Signals.',
    setupKind: 'in_product',
    sourceType: 'scrape_tsv',
  },
  {
    id: 'scrape_xml',
    name: 'XML via Scraper',
    category: 'custom',
    description: "Zmist polls an XML endpoint on a schedule; `recordPath` picks the repeating element.",
    setupKind: 'in_product',
    sourceType: 'scrape_xml',
  },
  {
    id: 'scrape_yaml',
    name: 'YAML via Scraper',
    category: 'custom',
    description: 'Zmist polls a YAML endpoint on a schedule and maps records to Signals.',
    setupKind: 'in_product',
    sourceType: 'scrape_yaml',
  },
  {
    id: 'scrape_prometheus',
    name: 'Prometheus via Scraper',
    category: 'metrics',
    description: 'Zmist polls a /metrics endpoint on a schedule and forwards samples - no agent to install.',
    setupKind: 'in_product',
    sourceType: 'scrape_prometheus',
  },
  {
    id: 'scrape_html',
    name: 'HTML table via Scraper',
    category: 'custom',
    description: 'Zmist polls a web page on a schedule and reads a table from it into Signals.',
    setupKind: 'in_product',
    sourceType: 'scrape_html',
  },
];

export const connectorCategoryLabel: Record<ConnectorCategory, string> = {
  engineering: 'Engineering',
  metrics: 'Metrics',
  business: 'Business',
  custom: 'Custom',
};

// Matches an existing Mapping back to its catalog entry, for display on the
// Integration detail shell (roadmap #9 Phase 1 slice 4). Webhook-family
// mappings disambiguate on spec.provider (github/stripe/generic).
//
// "prometheus" is genuinely ambiguous: Prometheus Remote Write and
// Prometheus scrape share one identical Mapping shape (same sourceType, no
// distinguishing field - see this file's header comment) and there is no
// way to tell which produced a given document. Guessing one over the other
// would mislabel real integrations, so this deliberately returns undefined
// there and lets the caller fall back to the raw sourceType string instead.
export function findConnectorForMapping(sourceType: string, provider?: string): ConnectorDefinition | undefined {
  const normalizedSourceType = sourceType.trim().toLowerCase();
  const normalizedProvider = (provider || 'generic').trim().toLowerCase();
  if (normalizedSourceType === 'webhook') {
    return connectorCatalog.find((c) => c.sourceType === 'webhook' && (c.provider || 'generic') === normalizedProvider);
  }
  const matches = connectorCatalog.filter((c) => c.sourceType === normalizedSourceType);
  return matches.length === 1 ? matches[0] : undefined;
}
